import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../types';
import { db, handleFirestoreError, OperationType, auth } from '../lib/firebase';
import { cleanIcsString, safeFormatISO, isZavalaExpired, safeUserSearch } from '../lib/utils';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs, query, where, setDoc, onSnapshot } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Users, 
  Calendar, 
  Trophy, 
  CheckCircle2,
  ExternalLink,
  Filter,
  RefreshCcw,
  Upload,
  Download,
  ShieldCheck,
  FileText
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import EventForm from './EventForm';
import MemberForm from './MemberForm';
import MergeUsersModal from './MergeUsersModal';
import LCARSConfirmationModal from './LCARSConfirmationModal';
import PromotionBoard from './PromotionBoard';
import { calculatePromotionStats, getEligibleRank } from '../lib/promotion';
import { ROLES, ROLE_PERMISSIONS } from '../constants';
import Papa from 'papaparse';

import RankManager from './RankManager';
import DepartmentManager from './DepartmentManager';
import IcsImporter from './IcsImporter';
import HelpDeskAdmin from './HelpDeskAdmin';
import ConPlannerMain from './ConPlanner/ConPlannerMain';
import SurveyManager from './SurveyManager';
import ErrorLogsViewer from './ErrorLogsViewer';
import ProfileChangeQueue from './ProfileChangeQueue';
import GuestManager from './GuestManager';
import ApiIntegrationsManager from './ApiIntegrationsManager';
interface AdminDashboardProps {
  users: UserProfile[];
  events: ClubEvent[];
  attendance: Attendance[];
  currentUserProfile: UserProfile | null;
  ranks: RankRequirement[];
  departments: { id: string, name: string }[];
  initialTab?: string;
  onSelectConvention?: (id: string) => void;
  onTabChange?: (tab: string) => void;
}

export const openReportInNewWindow = (title: string, headers: string[], rows: string[][], filename: string) => {
  const newWindow = window.open('', '_blank');
  if (!newWindow) {
    toast.error('Failed to open new window. Please check your browser pop-up settings.');
    return;
  }

  const escapeHtml = (str: string) => {
    if (str === undefined || str === null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const safeTitle = escapeHtml(title);
  const tableHeaders = headers.map(h => `<th style="text-align: left; padding: 12px 16px; border-bottom: 2px solid #38bdf8; color: #38bdf8; font-size: 0.75rem; text-transform: uppercase; font-weight: bold; letter-spacing: 1px;">${escapeHtml(h)}</th>`).join('');
  
  const tableRows = rows.map((row, idx) => {
    const bg = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)';
    return `<tr style="background-color: ${bg}; border-bottom: 1px solid rgba(255,255,255,0.05); transition: background-color 0.2s;">` + 
      row.map(cell => `<td style="padding: 12px 16px; font-size: 0.85rem; color: #ffffff;">${escapeHtml(cell)}</td>`).join('') +
      `</tr>`;
  }).join('');

  const escapeCsv = (val: string) => {
    if (val === undefined || val === null) return '';
    const s = String(val);
    const escaped = s.replace(/"/g, '""');
    if (escaped.includes(',') || escaped.includes('\n') || escaped.includes('"')) {
      return `"${escaped}"`;
    }
    return escaped;
  };

  const csvContent = [
    headers.map(escapeCsv).join(','),
    ...rows.map(r => r.map(escapeCsv).join(','))
  ].join('\n');

  const csvDataEncoded = encodeURIComponent(csvContent);

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${safeTitle}</title>
      <meta charset="utf-8">
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;700&display=swap" rel="stylesheet">
      <style>
        body {
          background-color: #000000;
          color: #ffffff;
          font-family: 'Outfit', sans-serif;
          margin: 0;
          padding: 32px;
        }
        .lcars-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background-color: #f59e0b;
          color: #000000;
          padding: 12px 24px;
          font-weight: bold;
          font-size: 1.2rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          border-radius: 9999px;
          margin-bottom: 32px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
        .btn-download {
          background-color: #a855f7;
          color: #ffffff;
          border: none;
          padding: 10px 24px;
          font-family: 'Outfit', sans-serif;
          font-size: 0.8rem;
          font-weight: bold;
          text-transform: uppercase;
          cursor: pointer;
          border-radius: 9999px;
          letter-spacing: 1px;
          transition: all 0.2s;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .btn-download:hover {
          background-color: #c084fc;
          transform: translateY(-1px);
        }
        .btn-download:active {
          transform: translateY(0);
        }
        .table-container {
          border: 2px solid rgba(56, 189, 248, 0.3);
          border-radius: 12px;
          overflow: hidden;
          background-color: #0b0f19;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        thead {
          background-color: rgba(56, 189, 248, 0.05);
        }
        tr:hover {
          background-color: rgba(56, 189, 248, 0.04) !important;
        }
      </style>
    </head>
    <body>
      <div class="lcars-bar">
        <div>${safeTitle}</div>
        <button class="btn-download" id="download-btn">Download CSV</button>
      </div>
      <div class="table-container">
        <table>
          <thead>
            <tr>${tableHeaders}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>
      <script>
        document.getElementById('download-btn').addEventListener('click', () => {
          const csvContent = decodeURIComponent(\`${csvDataEncoded}\`);
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = '${filename}';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        });
      </script>
    </body>
    </html>
  `;

  newWindow.document.write(html);
  newWindow.document.close();
};

const AdminDashboard = React.memo(function AdminDashboard({ users, events, attendance, currentUserProfile, ranks, departments, initialTab = 'members', onSelectConvention, onTabChange }: AdminDashboardProps) {
  const [memberSearch, setMemberSearch] = useState('');
  const [showExpiredPersonnel, setShowExpiredPersonnel] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const legacyFileInputRef = useRef<HTMLInputElement>(null);
  
  const [isEventFormOpen, setIsEventFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ClubEvent | null>(null);
  
  const [isMemberFormOpen, setIsMemberFormOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<UserProfile | null>(null);
  const [pendingProfileRequestsCount, setPendingProfileRequestsCount] = useState(0);
  const [selectedAttendanceEventId, setSelectedAttendanceEventId] = useState<string>('');

  useEffect(() => {
    const q = query(
      collection(db, 'profile_change_requests'),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingProfileRequestsCount(snap.docs.length);
    }, (err) => {
      // Ignore if user lacks permissions
    });
    return () => unsub();
  }, []);

  // Advanced Event State
  const [eventTimeFilter, setEventTimeFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const [eventFilterType, setEventFilterType] = useState<string>('all');
  const [eventSortBy, setEventSortBy] = useState<'date-desc' | 'date-asc' | 'name-asc' | 'name-desc'>('date-asc');
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());

  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'danger'
  });

  /*
  useEffect(() => {
    if (eventTimeFilter === 'upcoming' && eventSortBy !== 'date-asc') {
      setEventSortBy('date-asc');
    } else if (eventTimeFilter === 'past' && eventSortBy !== 'date-desc') {
      setEventSortBy('date-desc');
    }
  }, [eventTimeFilter]);
  */

  const askConfirmation = (title: string, description: string, onConfirm: () => void, variant: 'danger' | 'info' = 'danger') => {
    setConfirmDialog({
      isOpen: true,
      title,
      description,
      onConfirm,
      variant
    });
  };

  const handleSync = useCallback(() => {
    setIsRefreshing(true);
    // Data is already live via onSnapshot, but this provides visual confirmation
    // and ensures all derived calculations are fresh.
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success('Command Deck synchronized with shipboard sensors');
    }, 800);
  }, []);

  const handleSeed2026Events = () => {
    askConfirmation(
      'Initialize 2026 Missions',
      'This will populate the database with standard 2026 USS Zavala events. This action will supplement existing records.',
      async () => {
        setIsRefreshing(true);
        const events2026 = [
          // Chapter Meetings (3rd Saturday)
          { name: 'January Chapter Meeting', date: '2026-01-17T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'February Chapter Meeting', date: '2026-02-21T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'March Chapter Meeting', date: '2026-03-21T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'April Chapter Meeting', date: '2026-04-18T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'May Chapter Meeting', date: '2026-05-16T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'June Chapter Meeting', date: '2026-06-20T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'July Chapter Meeting', date: '2026-07-18T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'August Chapter Meeting', date: '2026-08-15T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'September Chapter Meeting', date: '2026-09-19T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'October Chapter Meeting', date: '2026-10-17T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'November Chapter Meeting', date: '2026-11-21T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          { name: 'December Chapter Meeting', date: '2026-12-19T13:00:00', type: 'Meeting', points: 5, location: 'IHOP - Houston' },
          
          // Social Events (1st Saturday)
          { name: 'New Year Social', date: '2026-01-03T18:00:00', type: 'Social', points: 3, location: 'Main Event' },
          { name: 'February Social', date: '2026-02-07T18:00:00', type: 'Social', points: 3, location: 'Dave & Busters' },
          { name: 'March Social', date: '2026-03-07T18:00:00', type: 'Social', points: 3, location: 'Top Golf' },
          { name: 'April Social', date: '2026-04-04T18:00:00', type: 'Social', points: 3, location: 'Movie Night' },
          { name: 'May Social', date: '2026-05-02T18:00:00', type: 'Social', points: 3, location: 'Bowling' },
          { name: 'July Social', date: '2026-07-04T18:00:00', type: 'Social', points: 3, location: 'Independence Day BBQ' },
          { name: 'August Social', date: '2026-08-01T18:00:00', type: 'Social', points: 3, location: 'Game Night' },
          { name: 'September Social', date: '2026-09-05T18:00:00', type: 'Social', points: 3, location: 'Laser Tag' },
          { name: 'October Social', date: '2026-10-03T18:00:00', type: 'Social', points: 3, location: 'Halloween Party' },
          { name: 'November Social', date: '2026-11-07T18:00:00', type: 'Social', points: 3, location: 'Board Game Night' },
          { name: 'December Social', date: '2026-12-05T18:00:00', type: 'Social', points: 3, location: 'Holiday Party' },

          // Region 3 Summit
          { name: 'Region 3 Summit 2026', date: '2026-06-19T09:00:00', endDate: '2026-06-21T17:00:00', type: 'Away Mission', points: 15, location: 'Killeen, Texas' },
          
          // Convention Example
          { 
            name: 'Houston Comic-Con', 
            date: '2026-05-22T10:00:00', 
            endDate: '2026-05-24T18:00:00', 
            type: 'Convention', 
            points: 20, 
            location: 'George R. Brown Convention Center',
            pertinentInfo: 'USS Zavala will have a table in the fan zone. Please sign up for shifts.',
            sessions: [
              { id: 's1', name: 'Star Trek Trivia', startTime: '14:00', endTime: '15:00', location: 'Room 302' },
              { id: 's2', name: 'Cosplay Contest', startTime: '16:00', endTime: '18:00', location: 'Main Stage' }
            ],
            schedule: [
              { id: 'sch1', time: '10:00', activity: 'Booth Setup', location: 'Fan Zone' },
              { id: 'sch2', time: '12:00', activity: 'Lunch Break' }
            ]
          },
        ];

        try {
          for (const event of events2026) {
            const exists = events.some(e => e.name === event.name && e.startDate.startsWith('2026'));
            if (!exists) {
              await addDoc(collection(db, 'events'), {
                name: event.name,
                startDate: event.date,
                endDate: event.endDate || null,
                type: event.type,
                points: event.points,
                location: event.location,
                status: 'active',
                pertinentInfo: (event as any).pertinentInfo || null,
                sessions: (event as any).sessions || null,
                schedule: (event as any).schedule || null
              });
            }
          }
          toast.success('2026 Missions loaded into computer core');
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, 'events');
        } finally {
          setIsRefreshing(false);
        }
      }
    );
  };

  const handleLegacyImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content);
        
        // Handle both single event or array of events
        const eventsToImport = Array.isArray(data) ? data : [data];
        
        setIsRefreshing(true);
        let successCount = 0;
        
        for (const legacyEvent of eventsToImport) {
          // Map legacy fields to current schema if needed
          const newEvent = {
            name: legacyEvent.name || legacyEvent.title || 'Legacy Mission',
            startDate: legacyEvent.startDate || legacyEvent.date || legacyEvent.start || new Date().toISOString(),
            endDate: legacyEvent.endDate || legacyEvent.end || null,
            location: legacyEvent.location || legacyEvent.venue || '',
            type: legacyEvent.type || 'Meeting',
            points: parseInt(legacyEvent.points) || 10,
            status: 'active',
            pertinentInfo: legacyEvent.pertinentInfo || legacyEvent.description || legacyEvent.info || null,
            sessions: legacyEvent.sessions || null,
            schedule: legacyEvent.schedule || null
          };
          
          await addDoc(collection(db, 'events'), newEvent);
          successCount++;
        }
        
        toast.success(`Successfully imported ${successCount} legacy missions`);
      } catch (err) {
        console.error(err);
        toast.error('Failed to parse legacy data. Ensure it is a valid JSON file.');
      } finally {
        setIsRefreshing(false);
        if (legacyFileInputRef.current) legacyFileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };
  
  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      error: (error) => {
        setIsRefreshing(false);
        toast.error(`CSV Parsing error: ${error.message}`);
      },
      complete: async (results) => {
        setIsRefreshing(true);
        let successCount = 0;
        let errorCount = 0;
        const skippedReasons: string[] = [];
        let rowIndex = 1;

        if (results.errors && results.errors.length > 0) {
          console.warn('CSV parser warnings:', results.errors);
        }

        for (const row of results.data as any[]) {
          rowIndex++;
          try {
            const sfiEmail = (
              row.EMAIL || 
              row.email || 
              row.Email || 
              row['SFI Email'] || 
              row['Sfi Email'] || 
              row['SFI Email Address'] || 
              row['Sfi Email Address'] || 
              row['SFI email address'] || 
              row['Sfi email address'] || 
              row.sfi_email || 
              row.SFI_EMAIL || 
              row.sfiEmail || 
              row.SfiEmail || 
              ''
            ).trim();
            const sccCol = (row.SCC || row.scc || row.sccNumber || row.SccNumber || row.sccId || row.SccId || row['SCC Number'] || row['SCC ID'] || '').trim();
            const points = (row.points || row.Points || '').trim();
            const joinVal = (row.JOIN || row.join || row.joinDate || row.JoinDate || row['Join Date'] || '').trim();
            const expVal = (row.EXP || row.exp || row.expireDate || row.ExpireDate || row['Expiration Date'] || '').trim();
            const roles = (row.roles || row.Roles || '').trim();
            const cell = (row.PHONE || row.phone || row.Phone || row.cell || row.Cell || row['Phone Number'] || '').trim();
            const rankVal = (row.rank || row.Rank || row.RANK || row.RANK_2 || row.RANK_1 || row.currentRank || row.CurrentRank || '').trim();
            const deptVal = (row.department || row.Department || '').trim();
            const dobVal = (row.DOB || row.dob || row['Date of Birth'] || '').trim();
            const otsVal = (row.OTS || row.ots || '').trim().toLowerCase();
            const occVal = (row.OCC || row.occ || '').trim().toLowerCase();

            // Clean & Split Name
            let firstName = (
              row.firstName || 
              row.FirstName || 
              row['First Name'] || 
              row.first_name || 
              row.First_Name || 
              row.firstname || 
              row.Firstname || 
              row.FIRST_NAME || 
              ''
            ).trim();
            
            let lastName = (
              row.lastName || 
              row.LastName || 
              row['Last Name'] || 
              row.last_name || 
              row.Last_Name || 
              row.lastname || 
              row.Lastname || 
              row.LAST_NAME || 
              ''
            ).trim();
            if (!firstName && !lastName) {
              const memberName = (row.MEMBER || row.member || row.Member || '').trim();
              if (memberName) {
                if (memberName.includes(',')) {
                  const parts = memberName.split(',');
                  lastName = parts[0].trim();
                  firstName = parts.slice(1).join(',').trim();
                } else {
                  const prefixes = ['capt', 'captain', 'cdr', 'cmdr', 'commander', 'ens', 'ensign', 'lt', 'lieutenant', 'adm', 'admiral', 'gen', 'general', 'ltjg', 'po1', 'po2', 'po3', 'cpo', 'scpo', 'mcpo', 'rec', 'crmn', 'crewman', 'po', 'petty officer'];
                  let words = memberName.split(/\s+/);
                  if (words.length > 1 && prefixes.includes(words[0].toLowerCase().replace(/\.$/, ''))) {
                    words = words.slice(1);
                  }
                  firstName = words[0] || '';
                  lastName = words.slice(1).join(' ') || '';
                }
              }
            }

            if (!sfiEmail) {
              skippedReasons.push(`Row ${rowIndex}: Missing SFI Email`);
              errorCount++;
              continue;
            }
            if (!firstName || !lastName) {
              skippedReasons.push(`Row ${rowIndex}: Missing name (Email: ${sfiEmail})`);
              errorCount++;
              continue;
            }

            // Normalize SCC
            let sccNumber = sccCol;
            if (sccNumber && /^\d+$/.test(sccNumber)) {
              sccNumber = `SCC-${sccNumber}`;
            }

            // Parse dates
            const parseSfiDate = (dateStr: string) => {
              if (!dateStr) return '';
              try {
                const parsed = new Date(dateStr);
                if (!isNaN(parsed.getTime())) {
                  return parsed.toISOString().split('T')[0];
                }
              } catch (e) {}
              return dateStr;
            };
            const sfiJoinDate = parseSfiDate(joinVal);
            const sfiExpireDate = parseSfiDate(expVal);
            const dob = parseSfiDate(dobVal);

            // Parse OTS/OCC
            const ots = ['yes', 'true', 'passed', 'y', '1'].includes(otsVal) || (otsVal !== '' && !['no', 'false', 'failed', 'n', '0'].includes(otsVal));
            const occ = ['yes', 'true', 'passed', 'y', '1'].includes(occVal) || (occVal !== '' && !['no', 'false', 'failed', 'n', '0'].includes(occVal));

            // Map rank name/shortName/ID to system rank ID
            let rankId = rankVal || 'CRMN';
            if (rankVal) {
              const foundRank = ranks.find(r => 
                r.id.toLowerCase() === rankVal.toLowerCase() ||
                r.name.toLowerCase() === rankVal.toLowerCase() ||
                r.shortName.toLowerCase() === rankVal.toLowerCase()
              );
              if (foundRank) {
                rankId = foundRank.id;
              }
            }

            // Map department name to system department name
            let deptName = deptVal || 'UNASSIGNED';
            if (deptVal) {
              const foundDept = departments.find(d => 
                d.id.toLowerCase() === deptVal.toLowerCase() ||
                d.name.toLowerCase() === deptVal.toLowerCase()
              );
              if (foundDept) {
                deptName = foundDept.name;
              }
            }

            // Query existing user
            let querySnapshot = { empty: true } as any;
            if (sccNumber) {
              const qScc = query(collection(db, 'users'), where('sccNumber', '==', sccNumber));
              querySnapshot = await getDocs(qScc);
            }
            if (querySnapshot.empty && sfiEmail) {
              const qSfiEmail = query(collection(db, 'users'), where('sfiEmail', '==', sfiEmail));
              querySnapshot = await getDocs(qSfiEmail);
            }
            if (querySnapshot.empty && sfiEmail) {
              const qEmail = query(collection(db, 'users'), where('email', '==', sfiEmail));
              querySnapshot = await getDocs(qEmail);
            }

            if (querySnapshot.empty) {
              // Create placeholder profile for a new user
              const newUserRef = doc(collection(db, 'users'));
              const placeholderId = newUserRef.id;

              const userData = {
                firstName,
                lastName,
                email: sfiEmail, // default login email, adopts during registration
                sfiEmail,
                sccNumber: sccNumber || '',
                manualPoints: parseInt(points) || 0,
                zavalaJoinDate: sfiJoinDate || new Date().toISOString().split('T')[0],
                sfiJoinDate,
                sfiExpireDate,
                roles: roles ? roles.split(',').map((r: string) => r.trim()) : ['member'],
                promotionRankDate: sfiJoinDate || new Date().toISOString().split('T')[0],
                currentRank: rankId,
                department: deptName,
                cell: cell || '',
                dob: dob || '',
                ots,
                occ,
                sdfpp: false,
                uid: placeholderId,
                status: 'active'
              };

              await setDoc(newUserRef, userData);
              successCount++;
            } else {
              const userDoc = querySnapshot.docs[0];
              const existingData = userDoc.data() as UserProfile;

              // Smart merge: update only missing/empty fields, preserve others!
              const mergedData: Partial<UserProfile> = {};

              if (!existingData.firstName && firstName) mergedData.firstName = firstName;
              if (!existingData.lastName && lastName) mergedData.lastName = lastName;
              if (!existingData.sccNumber && sccNumber) mergedData.sccNumber = sccNumber;
              if (!existingData.sfiEmail && sfiEmail) mergedData.sfiEmail = sfiEmail;
              if (!existingData.cell && cell) mergedData.cell = cell;
              if (!existingData.dob && dob) mergedData.dob = dob;
              if (!existingData.sfiJoinDate && sfiJoinDate) mergedData.sfiJoinDate = sfiJoinDate;
              if (!existingData.sfiExpireDate && sfiExpireDate) mergedData.sfiExpireDate = sfiExpireDate;
              if (!existingData.ots && ots) mergedData.ots = ots;
              if (!existingData.occ && occ) mergedData.occ = occ;

              if ((!existingData.department || existingData.department === 'UNASSIGNED') && deptName) {
                mergedData.department = deptName;
              }

              const defaultRanks = ['', 'REC', 'CRMN', 'COMM'];
              if ((!existingData.currentRank || defaultRanks.includes(existingData.currentRank)) && rankVal) {
                mergedData.currentRank = rankId;
              }

              if ((!existingData.zavalaJoinDate || existingData.zavalaJoinDate === new Date().toISOString().split('T')[0]) && sfiJoinDate) {
                mergedData.zavalaJoinDate = sfiJoinDate;
              }
              if ((!existingData.promotionRankDate || existingData.promotionRankDate === new Date().toISOString().split('T')[0]) && sfiJoinDate) {
                mergedData.promotionRankDate = sfiJoinDate;
              }

              if ((existingData.manualPoints === undefined || existingData.manualPoints === 0) && points) {
                mergedData.manualPoints = parseInt(points) || 0;
              }

              if ((!existingData.roles || existingData.roles.length === 0 || (existingData.roles.length === 1 && existingData.roles[0] === 'member')) && roles) {
                mergedData.roles = roles.split(',').map((r: string) => r.trim());
              }

              if (Object.keys(mergedData).length > 0) {
                await updateDoc(doc(db, 'users', userDoc.id), mergedData);
              }
              successCount++;
            }
          } catch (err) {
            console.error(err);
            const errMsg = err instanceof Error ? err.message : 'Unknown write error';
            skippedReasons.push(`Row ${rowIndex}: ${errMsg}`);
            errorCount++;
          }
        }

        setIsRefreshing(false);
        if (errorCount > 0) {
          toast.warning(`Import complete with warnings: ${successCount} processed, ${errorCount} skipped.`, {
            description: `Skipped rows detail:\n${skippedReasons.slice(0, 5).join('\n')}${skippedReasons.length > 5 ? '\n...and more' : ''}`,
            duration: 10000
          });
        } else {
          toast.success(`Import complete: ${successCount} processed successfully.`);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    });
  };

  const downloadCsvTemplate = () => {
    const headers = ['POS', 'RANK', 'MEMBER', 'SCC', 'USERNAME', 'PRIMARY', 'JOIN', 'EXP', 'DOB', 'PHONE', 'EMAIL', 'OTS', 'OCC'];
    const sample = ['CO', 'CAPT', 'James Kirk', '1701', 'jkirk', 'Yes', '2026-01-01', '2027-01-01', '1980-01-01', '555-0100', 'kirk@starfleet.com', 'Yes', 'Yes'];
    const csv = [headers, sample].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'member_import_template.csv');
    a.click();
  };

  const downloadRolesPermissionsCsv = () => {
    const headers = ['Role ID', 'Role Name', 'Permissions'];
    const rows = ROLES.map(role => [
      role.id,
      role.name,
      ROLE_PERMISSIONS[role.id].join('; ')
    ]);
    openReportInNewWindow('Roles and Permissions Configuration', headers, rows, 'roles_and_permissions.csv');
  };

  const downloadPersonnelReportCsv = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'SCC Number', 'Last Login Time', 'Last Login IP', 'Password Last Changed', 'Roles / Permissions'];
    const rows = users.map(u => [
      u.firstName || '',
      u.lastName || '',
      u.email || '',
      u.sccNumber || '',
      u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never',
      u.lastLoginIp || 'Unknown',
      u.passwordLastChanged ? new Date(u.passwordLastChanged).toLocaleString() : 'Never',
      (u.roles || []).join('; ')
    ]);
    openReportInNewWindow('Personnel Security Report', headers, rows, 'personnel_security_report.csv');
  };

  const downloadSfiExpiryReportCsv = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'SCC Number', 'SFI Expiration Date'];
    
    const today = new Date();
    const ninetyDaysLimit = new Date();
    ninetyDaysLimit.setDate(today.getDate() + 90);
    
    const expiringUsers = users.filter(u => {
      if (!u.sfiExpireDate) return false;
      const expireDate = new Date(u.sfiExpireDate);
      return expireDate <= ninetyDaysLimit;
    });
    
    // Sort ascending by expiration date
    expiringUsers.sort((a, b) => {
      const dateA = new Date(a.sfiExpireDate!).getTime();
      const dateB = new Date(b.sfiExpireDate!).getTime();
      return dateA - dateB;
    });
    
    const rows = expiringUsers.map(u => [
      u.firstName || '',
      u.lastName || '',
      u.email || '',
      u.sccNumber || '',
      u.sfiExpireDate || ''
    ]);
    openReportInNewWindow('SFI Membership Expiry Report', headers, rows, 'sfi_membership_expiry_report.csv');
  };

  const downloadDuesDelinquentReportCsv = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'SCC Number', 'Dues Paid Year', 'Status'];
    const currentYear = new Date().getFullYear();
    
    const delinquentUsers = users.filter(u => {
      return u.duesPaidYear !== currentYear;
    });
    
    const rows = delinquentUsers.map(u => [
      u.firstName || '',
      u.lastName || '',
      u.email || '',
      u.sccNumber || '',
      u.duesPaidYear ? String(u.duesPaidYear) : 'Never',
      u.status || 'active'
    ]);
    openReportInNewWindow('Chapter Dues Delinquent Report', headers, rows, 'chapter_dues_delinquent_report.csv');
  };

  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Exclude guests and volunteers from Zavala Crew manifest (managed in Guests & Volunteers tab)
      if (u.roles?.includes('guest') || u.isGuest || (u.shipName && !u.shipName.toLowerCase().includes('zavala'))) {
        return false;
      }
      // Hide expired personnel unless showExpiredPersonnel is toggled on
      if (!showExpiredPersonnel && isZavalaExpired(u.zavalaExpireDate)) {
        return false;
      }
      return safeUserSearch(u, memberSearch);
    });
  }, [users, memberSearch, showExpiredPersonnel]);

  const filteredEvents = useMemo(() => {
    const now = new Date().toISOString();
    return events.filter(e => {
      const matchesSearch = (e.name || '').toLowerCase().includes((eventSearch || '').trim().toLowerCase());
      const matchesType = eventFilterType === 'all' || e.type === eventFilterType;
      
      let matchesTime = true;
      if (eventTimeFilter === 'upcoming') {
        matchesTime = e.startDate >= now;
      } else if (eventTimeFilter === 'past') {
        matchesTime = e.startDate < now;
      }

      return matchesSearch && matchesType && matchesTime;
    }).sort((a, b) => {
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      
      if (eventSortBy === 'date-desc') return dateB - dateA;
      if (eventSortBy === 'date-asc') return dateA - dateB;
      if (eventSortBy === 'name-asc') return a.name.localeCompare(b.name);
      if (eventSortBy === 'name-desc') return b.name.localeCompare(a.name);
      return 0;
    });
  }, [events, eventSearch, eventFilterType, eventSortBy, eventTimeFilter]);

  const toggleEventSelection = (id: string) => {
    const newSelected = new Set(selectedEventIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEventIds(newSelected);
  };

  const toggleAllEvents = () => {
    if (selectedEventIds.size === filteredEvents.length) {
      setSelectedEventIds(new Set());
    } else {
      setSelectedEventIds(new Set(filteredEvents.map(e => e.id!)));
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedEventIds.size;
    askConfirmation(
      'De-materialize Missions',
      `Are you sure you want to delete ${count} missions? This action is irreversible and will remove all associated database records.`,
      async () => {
        setIsRefreshing(true);
        try {
          const idsToDelete = Array.from(selectedEventIds);
          for (const id of idsToDelete) {
            await deleteDoc(doc(db, 'events', id as string));
          }
          setSelectedEventIds(new Set());
          toast.success(`Successfully de-materialized ${count} missions.`);
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, 'events');
        } finally {
          setIsRefreshing(false);
        }
      }
    );
  };

  const handleBulkUpdatePoints = async () => {
    const count = selectedEventIds.size;
    const newPoints = prompt('Enter new point value for selected missions:');
    if (newPoints === null || isNaN(parseInt(newPoints))) return;

    setIsRefreshing(true);
    try {
      const idsToUpdate = Array.from(selectedEventIds);
      for (const id of idsToUpdate) {
        await updateDoc(doc(db, 'events', id as string), { points: parseInt(newPoints) });
      }
      setSelectedEventIds(new Set());
      toast.success(`Updated points for ${count} missions.`);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'events');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeleteEvent = async (id: string) => {
    askConfirmation(
      'Confirm De-materialization',
      'Are you sure you want to delete this event? This will also delete all attendance records for it and remove it from ship logs.',
      async () => {
        try {
          await deleteDoc(doc(db, 'events', id));
          toast.success('Mission de-materialized');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, 'events');
        }
      }
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 lg:gap-6">
        <div className="flex items-center gap-4">
          <div className="h-10 lg:h-12 w-3 lg:w-4 bg-lcars-gold rounded-full shrink-0"></div>
          <h2 className="text-2xl lg:text-4xl font-bold tracking-tighter text-lcars-gold whitespace-nowrap uppercase">COMMAND DECK</h2>
        </div>
        {/* Hidden inputs for file triggers */}
        <input 
          type="file" 
          ref={fileInputRef} 
          className="hidden" 
          accept=".csv" 
          onChange={handleCsvUpload} 
        />
        <input 
          type="file" 
          ref={legacyFileInputRef} 
          className="hidden" 
          accept=".json" 
          onChange={handleLegacyImport} 
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0">
          <div className="h-10 w-8 lg:w-24 bg-lcars-tan rounded-l-full shrink-0"></div>
          <TabsList className="bg-transparent h-auto p-0 flex gap-1.5 flex-nowrap">
            <TabsTrigger 
              value="members" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              CREW MANIFEST
            </TabsTrigger>
            <TabsTrigger 
              value="guests" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-black/60"
            >
              GUESTS & VOLUNTEERS
            </TabsTrigger>
            <TabsTrigger 
              value="profile-queue" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-black/60 relative"
            >
              PROFILE QUEUE
              {pendingProfileRequestsCount > 0 && (
                <span className="ml-1.5 bg-lcars-red text-[var(--foreground)] px-1.5 py-0.2 rounded-full text-[0.6rem] font-bold animate-pulse">
                  {pendingProfileRequestsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="promotions" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-pink data-[state=active]:text-black text-black/60"
            >
              PROMOTION BOARD
            </TabsTrigger>
            <TabsTrigger 
              value="events" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-black/60"
            >
              MISSION ADMIN
            </TabsTrigger>
            <TabsTrigger 
              value="con-admin" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-black/60"
            >
              CON ADMIN
            </TabsTrigger>

            <TabsTrigger 
              value="attendance" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              ATTENDANCE
            </TabsTrigger>
            <TabsTrigger 
              value="ranks" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-gold data-[state=active]:text-black text-black/60"
            >
              RANKS
            </TabsTrigger>
            <TabsTrigger 
              value="departments" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              DEPTS
            </TabsTrigger>
            <TabsTrigger 
              value="calendar" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              CALENDAR SYNC
            </TabsTrigger>
            <TabsTrigger 
              value="scan" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              QUICK SCAN
            </TabsTrigger>
            <TabsTrigger 
              value="helpdesk" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-red data-[state=active]:text-[var(--foreground)] text-black/60"
            >
              HELP DESK
            </TabsTrigger>
            <TabsTrigger 
              value="surveys" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              SURVEYS
            </TabsTrigger>
            <TabsTrigger 
              value="diagnostics" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-red data-[state=active]:text-[var(--foreground)] text-black/60 relative overflow-visible"
            >
              DIAGNOSTICS
            </TabsTrigger>
            <TabsTrigger 
              value="api-relay" 
              className="lcars-pill bg-lcars-tan data-[state=active]:bg-lcars-blue data-[state=active]:text-black text-black/60"
            >
              API & STARFLEET
            </TabsTrigger>
          </TabsList>
          <div className="flex-1 h-10 bg-lcars-tan rounded-r-full"></div>
        </div>

        <TabsContent value="members" className="space-y-4 outline-none">
          <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-blue pl-3 lg:pl-6 py-2">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <span className="text-xl font-bold text-lcars-blue uppercase">USS Zavala Crew Manifest</span>
                  <p className="text-[0.65rem] text-[var(--foreground)]/50 uppercase font-mono mt-0.5">
                    Official chapter personnel, ranks, and promotion tracking
                  </p>
                </div>
                <button 
                  onClick={() => setActiveTab('guests')}
                  className="lcars-pill bg-lcars-gold/20 text-lcars-gold border border-lcars-gold/40 text-[0.65rem] py-1 px-3 hover:bg-lcars-gold hover:text-black transition-all flex items-center gap-1 font-bold"
                  title="Switch to Guests and Convention Volunteers"
                >
                  <Users className="w-3.5 h-3.5" /> GUESTS & VOLUNTEERS ({users.filter(u => u.roles?.includes('guest') || u.isGuest || (u.shipName && !u.shipName.toLowerCase().includes('zavala'))).length}) →
                </button>
                <div className="flex gap-2">
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isRefreshing}
                    className="lcars-pill bg-lcars-tan text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <Upload className="w-3 h-3" /> IMPORT
                  </button>
                  <button 
                    onClick={downloadCsvTemplate}
                    className="lcars-pill bg-lcars-tan text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <Download className="w-3 h-3" /> TEMPLATE
                  </button>
                  <button 
                    onClick={downloadRolesPermissionsCsv}
                    className="lcars-pill bg-lcars-tan text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <ShieldCheck className="w-3 h-3" /> ROLES
                  </button>
                  <button 
                    onClick={downloadPersonnelReportCsv}
                    className="lcars-pill bg-lcars-gold text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <Download className="w-3 h-3" /> REPORT
                  </button>
                  <button 
                    onClick={downloadSfiExpiryReportCsv}
                    className="lcars-pill bg-lcars-pink text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                    title="Members with SFI expiring in 90 days"
                  >
                    <Download className="w-3 h-3" /> SFI EXPIRES
                  </button>
                  <button 
                    onClick={downloadDuesDelinquentReportCsv}
                    className="lcars-pill bg-lcars-purple text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                    title="Members delinquent on chapter dues"
                  >
                    <Download className="w-3 h-3" /> DUES DELINQ
                  </button>
                  <button 
                    onClick={() => setIsMergeModalOpen(true)}
                    className="lcars-pill bg-lcars-gold text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <RefreshCcw className="w-3 h-3" /> MERGE CREW
                  </button>
                  <button 
                    onClick={() => { setEditingMember(null); setIsMemberFormOpen(true); }}
                    className="lcars-pill bg-lcars-blue text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <Plus className="w-3 h-3" /> ADD CREW
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full lg:w-auto">
                <div className="flex items-center gap-2 px-3 py-1.5 border border-lcars-red/30 rounded-full bg-lcars-red/5">
                  <input
                    type="checkbox"
                    id="showExpiredCheckbox"
                    checked={showExpiredPersonnel}
                    onChange={(e) => setShowExpiredPersonnel(e.target.checked)}
                    className="w-4 h-4 rounded border-lcars-red text-lcars-red focus:ring-lcars-red/50 bg-[var(--background)] cursor-pointer accent-lcars-red"
                  />
                  <label htmlFor="showExpiredCheckbox" className="text-[0.65rem] font-bold text-lcars-red tracking-wider uppercase cursor-pointer select-none">
                    Show Expired/Historic
                  </label>
                </div>
                <div className="relative w-full lg:w-64">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-lcars-blue" />
                  <input 
                    placeholder="SEARCH PERSONNEL..." 
                    className="w-full pl-10 bg-[var(--background)] border-2 border-lcars-blue text-[var(--foreground)] py-1.5 px-4 rounded-full text-base md:text-sm focus:bg-lcars-blue/10 outline-none transition-all"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto -mx-3 pl-3">
              <table className="w-full border-collapse min-w-[700px]">
                <thead>
                  <tr className="text-lcars-blue text-[0.7rem] font-bold tracking-widest uppercase">
                    <th className="text-left p-4 border-b-2 border-lcars-blue/30">NAME</th>
                    <th className="text-left p-4 border-b-2 border-lcars-blue/30">RANK</th>
                    <th className="text-left p-4 border-b-2 border-lcars-blue/30">DEPARTMENT</th>
                    <th className="text-left p-4 border-b-2 border-lcars-blue/30">POINTS</th>
                    <th className="text-left p-4 border-b-2 border-lcars-blue/30">STATUS</th>
                    <th className="text-right p-4 border-b-2 border-lcars-blue/30">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-lcars-blue/10">
                  {filteredUsers.map(member => {
                    const stats = calculatePromotionStats(member, events, attendance);
                    const eligibleRank = getEligibleRank(member, stats, ranks);
                    const currentRankIndex = ranks.findIndex(r => r.id === member.currentRank || (member.currentRank === 'CDR' && r.id === 'CMDR'));
                    const eligibleRankIndex = ranks.findIndex(r => r.id === eligibleRank.id);
                    const isEligible = eligibleRankIndex > currentRankIndex;

                    return (
                      <tr key={member.uid} className="hover:bg-lcars-blue/5 transition-colors group">
                        <td className="p-4">
                          <div className="font-bold text-lg group-hover:text-lcars-blue transition-colors flex items-center gap-2">
                            {member.firstName} {member.lastName}
                            {member.roles?.includes('read_only') && (
                              <span className="bg-lcars-red text-[var(--foreground)] text-[0.6rem] px-2 py-0.5 rounded-full animate-pulse tracking-widest uppercase">
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="text-[0.65rem] text-[var(--foreground)]/40">{member.email}</div>
                        </td>
                        <td className="p-4">
                          <span className="bg-lcars-blue text-black px-3 py-0.5 rounded-full font-bold text-xs">
                            {ranks.find(r => r.id === member.currentRank || (member.currentRank === 'CDR' && r.id === 'CMDR'))?.shortName || member.currentRank}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {member.departments && member.departments.length > 0 ? (
                              member.departments.map(d => (
                                <span key={d} className="text-lcars-blue font-bold text-xs uppercase bg-lcars-blue/10 px-2 py-0.5 rounded-sm">
                                  {d}
                                </span>
                              ))
                            ) : (
                              <span className="text-lcars-blue font-bold text-xs uppercase">
                                {member.department || 'UNASSIGNED'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-lcars-gold" />
                            <span className="font-bold text-xl">{stats.totalPoints}</span>
                          </div>
                        </td>
                        <td className="p-4">
                           {member.status === 'inactive' ? (
                            <span className="bg-white/20 text-[var(--foreground)]/50 px-3 py-0.5 rounded-full font-bold text-[0.7rem] uppercase">
                              INACTIVE
                            </span>
                          ) : isZavalaExpired(member.zavalaExpireDate) ? (
                            <span className="bg-lcars-red text-[var(--foreground)] px-3 py-0.5 rounded-full font-bold text-[0.7rem] uppercase">
                              EXPIRED
                            </span>
                          ) : (member.roles?.includes('guest') && (!member.shipName || !member.shipName.toLowerCase().includes('zavala'))) ? (
                            <span className="bg-lcars-blue/20 text-lcars-blue px-3 py-0.5 rounded-full font-bold text-[0.7rem] uppercase">
                              VOLUNTEER
                            </span>
                          ) : member.roles?.includes('read_only') ? (
                            <span className="bg-lcars-orange text-black px-3 py-0.5 rounded-full font-bold text-[0.7rem] animate-pulse uppercase">
                              NEW REGISTRATION (PENDING PERMS)
                            </span>
                          ) : !member.lastLogin ? (
                            <span className="bg-lcars-gold/20 text-lcars-gold px-3 py-0.5 rounded-full font-bold text-[0.7rem] uppercase">
                              PENDING SIGN-UP
                            </span>
                          ) : isEligible ? (
                            <span className="bg-lcars-red text-[var(--foreground)] px-3 py-0.5 rounded-full font-bold text-[0.7rem] animate-pulse">
                              ELIGIBLE: {eligibleRank.shortName}
                            </span>
                          ) : (
                            <span className="text-[0.7rem] text-[var(--foreground)]/40 font-bold">ACTIVE DUTY</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <button 
                            onClick={() => { setEditingMember(member); setIsMemberFormOpen(true); }}
                            className="lcars-pill bg-lcars-purple text-black px-4 py-1 text-xs font-bold"
                          >
                            MODIFY
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="guests" className="space-y-4 outline-none">
          <GuestManager 
            users={users} 
            events={events} 
            attendance={attendance} 
            ranks={ranks} 
            onSwitchToCrew={() => setActiveTab('members')}
          />
        </TabsContent>

        <TabsContent value="promotions" className="space-y-4 outline-none">
          <PromotionBoard users={users} events={events} attendance={attendance} ranks={ranks} />
        </TabsContent>

        <TabsContent value="events" className="space-y-4 outline-none">
          <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-gold pl-3 lg:pl-6 py-2">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-xl font-bold text-lcars-gold uppercase">Mission Admin</span>
                <div className="flex gap-2 flex-wrap">
                  <button 
                    onClick={() => { setEditingEvent(null); setIsEventFormOpen(true); }}
                    className="lcars-pill bg-lcars-blue text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <Plus className="w-3 h-3" /> NEW MISSION
                  </button>
                  <button 
                    onClick={handleSeed2026Events}
                    disabled={isRefreshing}
                    className="lcars-pill bg-lcars-pink text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <Calendar className="w-3 h-3" /> LOAD 2026
                  </button>
                  <button 
                    onClick={() => legacyFileInputRef.current?.click()}
                    disabled={isRefreshing}
                    className="lcars-pill bg-lcars-gold text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <RefreshCcw className="w-3 h-3" /> LEGACY
                  </button>
                  <button 
                    onClick={handleSync}
                    disabled={isRefreshing}
                    className="lcars-pill bg-lcars-purple text-black text-[0.6rem] py-1 px-3 flex items-center gap-1.5"
                  >
                    <RefreshCcw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} /> SYNC
                  </button>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4 w-full xl:w-auto items-start lg:items-center">
                <div className="relative w-full lg:w-48">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-lcars-gold" />
                  <input 
                    placeholder="SEARCH MISSIONS..." 
                    className="w-full pl-10 bg-[var(--background)] border-2 border-lcars-gold text-[var(--foreground)] py-1.5 px-4 rounded-full text-base md:text-[0.7rem] font-bold focus:bg-lcars-gold/10 outline-none transition-all placeholder:text-[var(--foreground)]/20"
                    value={eventSearch}
                    onChange={(e) => setEventSearch(e.target.value)}
                  />
                </div>

                <div className="flex gap-1 bg-lcars-tan/10 p-1 rounded-full border border-lcars-tan overflow-x-auto w-full lg:w-auto">
                  {(['all', 'upcoming', 'past'] as const).map((f) => (
                    <button
                      key={f}
                      onClick={() => {
                        setEventTimeFilter(f);
                        if (f === 'upcoming') setEventSortBy('date-asc');
                        if (f === 'past') setEventSortBy('date-desc');
                      }}
                      className={`lcars-pill text-[0.6rem] py-1 px-4 whitespace-nowrap px-4 py-1 rounded-full transition-all ${
                        eventTimeFilter === f ? 'bg-lcars-gold text-black' : 'text-lcars-tan hover:bg-lcars-tan/20'
                      }`}
                    >
                      {f.toUpperCase()}
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-4 w-full lg:w-auto">
                  <select 
                    className="flex-1 lg:flex-none bg-[var(--background)] border-2 border-lcars-gold text-[var(--foreground)] py-1.5 px-4 rounded-full text-base md:text-xs font-bold uppercase outline-none focus:bg-lcars-gold/10"
                    value={eventFilterType}
                    onChange={(e) => setEventFilterType(e.target.value)}
                  >
                  <option value="all">ALL TYPES</option>
                  {Array.from(new Set(events.map(e => e.type))).map(type => (
                    <option key={type} value={type}>{type.toUpperCase()}</option>
                  ))}
                </select>

                <select 
                  className="bg-[var(--background)] border-2 border-lcars-gold text-[var(--foreground)] py-1.5 px-4 rounded-full text-base md:text-xs font-bold uppercase outline-none focus:bg-lcars-gold/10"
                  value={eventSortBy}
                  onChange={(e) => setEventSortBy(e.target.value as any)}
                >
                  <option value="date-desc">LATEST STARDATE (DEC → JAN)</option>
                  <option value="date-asc">EARLIEST STARDATE (JAN → DEC)</option>
                  <option value="name-asc">MISSION NAME (A-Z)</option>
                  <option value="name-desc">MISSION NAME (Z-A)</option>
                </select>
              </div>
            </div>
          </div>

          {selectedEventIds.size > 0 && (
              <div className="mb-4 p-3 bg-lcars-gold/20 border border-lcars-gold/40 rounded-lg flex items-center justify-between animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center gap-4">
                  <span className="bg-lcars-gold text-black px-3 py-1 rounded-full font-bold text-xs uppercase">
                    {selectedEventIds.size} SELECTED
                  </span>
                  <p className="text-sm font-bold text-lcars-gold">BULK ACTIONS ENGAGED</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={handleBulkUpdatePoints}
                    className="lcars-pill bg-lcars-blue text-black px-4 py-1.5 text-xs font-bold"
                  >
                    UPDATE POINTS
                  </button>
                  <button 
                    onClick={handleBulkDelete}
                    className="lcars-pill bg-lcars-red text-[var(--foreground)] px-4 py-1.5 text-xs font-bold"
                  >
                    DELETE SELECTED
                  </button>
                  <button 
                    onClick={() => setSelectedEventIds(new Set())}
                    className="lcars-pill bg-white/10 text-[var(--foreground)] px-4 py-1.5 text-xs font-bold"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto -mx-3 pl-3">
              <table className="w-full border-collapse min-w-[800px]">
                <thead>
                  <tr className="text-lcars-gold text-[0.7rem] font-bold tracking-widest uppercase">
                    <th className="p-4 border-b-2 border-lcars-gold/30 text-left w-10">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 accent-lcars-gold cursor-pointer"
                        checked={selectedEventIds.size === filteredEvents.length && filteredEvents.length > 0}
                        onChange={toggleAllEvents}
                      />
                    </th>
                    <th className="text-left p-4 border-b-2 border-lcars-gold/30">MISSION NAME</th>
                    <th className="text-left p-4 border-b-2 border-lcars-gold/30">STARDATE</th>
                    <th className="text-left p-4 border-b-2 border-lcars-gold/30">TYPE</th>
                    <th className="text-left p-4 border-b-2 border-lcars-gold/30">POINTS</th>
                    <th className="text-right p-4 border-b-2 border-lcars-gold/30">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-lcars-gold/10">
                  {filteredEvents.map(event => (
                    <tr key={event.id} className={`hover:bg-lcars-gold/5 transition-colors group ${selectedEventIds.has(event.id!) ? 'bg-lcars-gold/10' : ''}`}>
                      <td className="p-4 border-b border-lcars-gold/5">
                        <input 
                          type="checkbox" 
                          className="w-4 h-4 accent-lcars-gold cursor-pointer"
                          checked={selectedEventIds.has(event.id!)}
                          onChange={() => toggleEventSelection(event.id!)}
                        />
                      </td>
                      <td className="p-4 font-bold text-lg group-hover:text-lcars-gold transition-colors">{cleanIcsString(event.name)}</td>
                      <td className="p-4 text-[var(--foreground)]/60 font-bold text-sm">{safeFormatISO(event.startDate, 'yyyy.MM.dd', 'TBD')}</td>
                      <td className="p-4">
                        <span className="bg-lcars-tan text-black px-3 py-0.5 rounded-full font-bold text-[0.7rem]">
                          {event.type}
                        </span>
                      </td>
                      <td className="p-4 font-bold text-xl text-lcars-blue">{event.points}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedAttendanceEventId(event.id!);
                              setActiveTab('attendance');
                            }}
                            className="lcars-pill bg-lcars-gold text-black px-3 py-1 text-xs font-bold"
                            title="Manage Attendees & Award Points"
                          >
                            ATTENDEES ({attendance.filter(a => a.eventId === event.id && a.status === 'attending').length})
                          </button>
                          {event.type === 'Convention' ? (
                            <button 
                              onClick={() => {
                                if (onSelectConvention && onTabChange) {
                                  onSelectConvention(event.id!);
                                  onTabChange('con-planner');
                                }
                              }}
                              className="lcars-pill bg-lcars-blue text-black px-4 py-1 text-xs font-bold"
                            >
                              PLANNER
                            </button>
                          ) : (
                            <button 
                              onClick={() => { setEditingEvent(event); setIsEventFormOpen(true); }}
                              className="lcars-pill bg-lcars-purple text-black px-4 py-1 text-xs font-bold"
                            >
                              EDIT
                            </button>
                          )}
                          <button 
                            onClick={() => handleDeleteEvent(event.id!)}
                            className="lcars-pill bg-lcars-red text-[var(--foreground)] px-4 py-1 text-xs font-bold"
                          >
                            DELETE
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="con-admin" className="space-y-4 outline-none h-[calc(100vh-250px)]">
          <ConPlannerMain 
            currentUserProfile={currentUserProfile} 
            isPublicMode={false}
          />
        </TabsContent>



        <TabsContent value="attendance" className="space-y-4 outline-none">
           <AttendanceTracker 
             events={events} 
             users={users} 
             attendance={attendance} 
             initialEventId={selectedAttendanceEventId}
             onSelectEventId={setSelectedAttendanceEventId}
           />
        </TabsContent>

        <TabsContent value="ranks" className="space-y-4 outline-none">
           <RankManager ranks={ranks} />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4 outline-none">
           <IcsImporter />
        </TabsContent>

        <TabsContent value="scan" className="space-y-4 outline-none">
          <div className="bg-[var(--background)] border-l-8 border-lcars-purple pl-6 py-2">
            <div className="flex justify-between items-center mb-6">
              <span className="text-xl font-bold text-lcars-purple">PERSONNEL QUICK SCAN</span>
              <span className="text-[0.7rem] text-[var(--foreground)]/40 font-bold tracking-widest">DISPLAYING {users.length} ACTIVE MEMBERS</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {users.map(u => {
                const stats = calculatePromotionStats(u, events, attendance);
                return (
                  <div key={u.uid} className="p-4 bg-lcars-purple/10 rounded-r-full border-l-4 border-lcars-purple flex items-center justify-between hover:bg-lcars-purple/20 transition-all group cursor-default">
                    <div>
                      <p className="font-bold text-lg group-hover:text-lcars-purple transition-colors">{u.firstName} {u.lastName}</p>
                      <p className="text-[0.7rem] text-[var(--foreground)]/40 font-bold tracking-widest">{u.currentRank}</p>
                    </div>
                    <div className="text-right pr-4">
                      <p className="text-2xl font-bold text-lcars-gold">{stats.totalPoints}</p>
                      <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-tighter">POINTS</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="departments" className="space-y-4 outline-none">
          <DepartmentManager departments={departments || []} />
        </TabsContent>

        <TabsContent value="helpdesk" className="space-y-4 outline-none">
          <HelpDeskAdmin />
        </TabsContent>

        <TabsContent value="surveys" className="space-y-4 outline-none">
          <SurveyManager />
        </TabsContent>

        <TabsContent value="profile-queue" className="space-y-4 outline-none">
          <ProfileChangeQueue currentUserProfile={currentUserProfile} allUsers={users} />
        </TabsContent>

        <TabsContent value="diagnostics" className="space-y-4 outline-none">
          <ErrorLogsViewer />
        </TabsContent>

        <TabsContent value="api-relay" className="space-y-4 outline-none">
          <ApiIntegrationsManager />
        </TabsContent>
      </Tabs>

      <EventForm 
        isOpen={isEventFormOpen} 
        onClose={() => setIsEventFormOpen(false)} 
        event={editingEvent} 
        users={users}
      />
      
      <MemberForm 
        isOpen={isMemberFormOpen} 
        onClose={() => setIsMemberFormOpen(false)} 
        member={editingMember} 
        currentUserProfile={currentUserProfile}
        ranks={ranks}
        departments={departments}
      />

      <MergeUsersModal 
        isOpen={isMergeModalOpen} 
        onClose={() => setIsMergeModalOpen(false)} 
        users={users} 
        attendance={attendance} 
      />

      <LCARSConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
      />
    </div>
  );
});

function AttendanceTracker({ 
  events, 
  users, 
  attendance, 
  initialEventId, 
  onSelectEventId 
}: { 
  events: ClubEvent[]; 
  users: UserProfile[]; 
  attendance: Attendance[]; 
  initialEventId?: string;
  onSelectEventId?: (id: string) => void;
}) {
  const [selectedEventId, setSelectedEventId] = useState<string>(initialEventId || events[0]?.id || '');

  useEffect(() => {
    if (initialEventId) {
      setSelectedEventId(initialEventId);
    }
  }, [initialEventId]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
    variant: 'danger'
  });

  const selectedEvent = events.find(e => e.id === selectedEventId);
  const [isDebriefEditing, setIsDebriefEditing] = useState(false);
  const [eventSummary, setEventSummary] = useState(selectedEvent?.summary || '');
  const [eventLessonsLearned, setEventLessonsLearned] = useState(selectedEvent?.lessonsLearned || '');
  const [isSavingDebrief, setIsSavingDebrief] = useState(false);

  useEffect(() => {
    if (selectedEvent) {
      setEventSummary(selectedEvent.summary || '');
      setEventLessonsLearned(selectedEvent.lessonsLearned || '');
    }
  }, [selectedEventId, selectedEvent]);

  const attendees = attendance
    .filter(a => a.eventId === selectedEventId && a.status === 'attending')
    .map(a => {
      const user = users.find(u => u.uid === a.userId);
      return user ? { user, attendanceId: a.id } : null;
    })
    .filter(Boolean) as { user: UserProfile; attendanceId: string }[];

  const pendingAttendees = attendance
    .filter(a => a.eventId === selectedEventId && a.status === 'pending')
    .map(a => {
      const user = users.find(u => u.uid === a.userId);
      return user ? { user, attendanceId: a.id, timestamp: a.timestamp } : null;
    })
    .filter(Boolean) as { user: UserProfile; attendanceId: string; timestamp: string }[];

  const attendingUserIds = new Set([...attendees.map(a => a.user.uid), ...pendingAttendees.map(a => a.user.uid)]);

  const handleConfirmAttendance = async (attendanceId: string, name: string) => {
    try {
      await updateDoc(doc(db, 'attendance', attendanceId), {
        status: 'attending',
        confirmedAt: new Date().toISOString()
      });
      toast.success(`Attendance confirmed for ${name}! (+${selectedEvent?.points || 10} pts awarded)`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to confirm attendance.');
    }
  };

  const handleConfirmAllPending = async () => {
    if (pendingAttendees.length === 0) return;
    try {
      const promises = pendingAttendees.map(p => 
        updateDoc(doc(db, 'attendance', p.attendanceId), {
          status: 'attending',
          confirmedAt: new Date().toISOString()
        })
      );
      await Promise.all(promises);
      toast.success(`Confirmed attendance for ${pendingAttendees.length} crew member(s)! Points awarded.`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to confirm pending attendance.');
    }
  };

  const handleSaveDebrief = async () => {
    if (!selectedEvent?.id) return;
    setIsSavingDebrief(true);
    try {
      await updateDoc(doc(db, 'events', selectedEvent.id), {
        summary: eventSummary,
        lessonsLearned: eventLessonsLearned
      });
      toast.success('Mission debrief and lessons learned saved.');
      setIsDebriefEditing(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to save debrief.');
    } finally {
      setIsSavingDebrief(false);
    }
  };

  const handleRemoveAttendance = async (attendanceId: string, name: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Attendance Record',
      description: `Are you sure you want to remove ${name} from this event's attendance log?`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'attendance', attendanceId));
          toast.success(`${name} has been removed from the attendance list.`);
        } catch (error) {
          console.error(error);
          toast.error(`Error removing attendee: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleAddAttendees = async () => {
    if (!selectedEvent?.id || selectedUserIds.size === 0) return;
    setIsSubmitting(true);

    try {
      const promises = Array.from(selectedUserIds).map(uid => 
        addDoc(collection(db, 'attendance'), {
          userId: uid,
          eventId: selectedEvent.id,
          status: 'attending',
          confirmedAt: new Date().toISOString(),
          timestamp: new Date().toISOString()
        })
      );
      await Promise.all(promises);
      toast.success(`Logged and confirmed attendance for ${selectedUserIds.size} crew member(s).`);
      setSelectedUserIds(new Set());
      setIsAddModalOpen(false);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'attendance');
      toast.error('Failed to record attendance.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleUserSelection = (uid: string) => {
    const next = new Set(selectedUserIds);
    if (next.has(uid)) {
      next.delete(uid);
    } else {
      next.add(uid);
    }
    setSelectedUserIds(next);
  };

  const filteredUsers = users.filter(u => safeUserSearch(u, memberSearch));

  return (
    <div className="bg-[var(--background)] border-l-8 border-lcars-tan pl-4 lg:pl-6 py-2">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <span className="text-xl font-bold text-lcars-tan uppercase">Attendance & Mission Debrief</span>
          <p className="text-[0.65rem] text-[var(--foreground)]/50 uppercase font-mono mt-0.5">
            Verify member attendance, award points, and document mission outcomes
          </p>
        </div>
        <select 
          className="w-full lg:w-auto bg-[var(--background)] border-2 border-lcars-tan rounded-full px-4 py-1.5 text-base md:text-sm text-[var(--foreground)] focus:bg-lcars-tan/10 outline-none transition-all font-bold"
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          {events.map(e => (
            <option key={e.id} value={e.id}>{cleanIcsString(e.name)} ({safeFormatISO(e.startDate, 'yyyy.MM.dd', 'TBD')})</option>
          ))}
        </select>
      </div>
      
      {selectedEvent ? (
        <div className="space-y-6">
          {/* Header Stats & Action Bar */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between bg-lcars-tan/10 p-4 lg:p-6 rounded-r-full border-l-4 border-lcars-tan gap-6">
            <div className="flex items-center gap-6">
              <div className="p-3 bg-lcars-tan text-black rounded-full shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[0.6rem] lg:text-[0.7rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Confirmed Personnel Present</p>
                <p className="text-2xl lg:text-3xl font-bold text-lcars-tan">{attendees.length}</p>
              </div>
              {pendingAttendees.length > 0 && (
                <div className="border-l-2 border-lcars-gold/40 pl-4">
                  <p className="text-[0.6rem] lg:text-[0.7rem] text-lcars-gold font-bold tracking-widest uppercase animate-pulse">Pending Confirmation</p>
                  <p className="text-2xl lg:text-3xl font-bold text-lcars-gold">{pendingAttendees.length}</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto lg:mr-8">
              <button 
                className="lcars-pill bg-lcars-gold text-black flex items-center gap-2 px-4 py-1.5 font-bold text-xs uppercase"
                onClick={() => {
                  setSelectedUserIds(new Set());
                  setMemberSearch('');
                  setIsAddModalOpen(true);
                }}
              >
                <Plus className="w-4 h-4" /> ADD ATTENDEE(S)
              </button>
              <button 
                className="lcars-pill bg-lcars-purple text-black flex items-center gap-2 px-4 py-1.5 font-bold text-xs uppercase"
                onClick={() => setIsDebriefEditing(!isDebriefEditing)}
              >
                <FileText className="w-4 h-4" /> {isDebriefEditing ? 'CLOSE DEBRIEF' : 'EDIT DEBRIEF'}
              </button>
              <button 
                className="lcars-pill bg-lcars-blue text-black flex items-center gap-2 px-4 py-1.5 font-bold text-xs uppercase"
                onClick={() => {
                  const cleanName = cleanIcsString(selectedEvent.name);
                  const headers = ['First Name', 'Last Name', 'Email', 'Cell', 'Event Name', 'Status'];
                  const rows = [
                    ...attendees.map(({ user: u }) => [u.firstName, u.lastName, u.email, u.cell || '', cleanName, 'Confirmed']),
                    ...pendingAttendees.map(({ user: u }) => [u.firstName, u.lastName, u.email, u.cell || '', cleanName, 'Pending'])
                  ];
                  openReportInNewWindow(`Attendance: ${cleanName}`, headers, rows, `attendance-${cleanName}.csv`);
                }}
              >
                <ExternalLink className="w-4 h-4" /> EXPORT CSV
              </button>
            </div>
          </div>

          {/* Pending Attendees Review Queue */}
          {pendingAttendees.length > 0 && (
            <div className="bg-lcars-gold/10 border-2 border-lcars-gold rounded-xl p-4 lg:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-lcars-gold/30 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-lcars-gold animate-ping" />
                  <div>
                    <h3 className="text-base lg:text-lg font-bold text-lcars-gold uppercase tracking-wider">
                      Self-Reported Attendance Queue ({pendingAttendees.length} Pending)
                    </h3>
                    <p className="text-xs text-[var(--foreground)]/70 font-sans">
                      These members reported attending this mission. Confirm their presence to award {selectedEvent.points} promotion points.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleConfirmAllPending}
                  className="lcars-pill bg-lcars-gold text-black px-4 py-1.5 text-xs font-bold shrink-0 hover:bg-white transition-colors"
                >
                  CONFIRM ALL PENDING ({pendingAttendees.length})
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {pendingAttendees.map(({ user: u, attendanceId }) => (
                  <div 
                    key={u.uid}
                    className="p-3 bg-black/40 border border-lcars-gold/40 rounded-lg flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-bold text-sm text-[var(--foreground)]">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-xs text-[var(--foreground)]/50 font-mono">
                        {u.email} {u.currentRank ? `• ${u.currentRank}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleConfirmAttendance(attendanceId, `${u.firstName} ${u.lastName}`)}
                        className="lcars-pill bg-lcars-blue text-black px-3 py-1 text-xs font-bold hover:bg-white transition-colors"
                      >
                        CONFIRM (+PTS)
                      </button>
                      <button
                        onClick={() => handleRemoveAttendance(attendanceId, `${u.firstName} ${u.lastName}`)}
                        className="p-1 text-lcars-red hover:bg-lcars-red/20 rounded transition-colors"
                        title="Reject Attendance"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mission Debrief & Lessons Learned Section */}
          {(isDebriefEditing || selectedEvent.summary || selectedEvent.lessonsLearned) && (
            <div className="bg-black/30 border-2 border-lcars-blue/40 rounded-xl p-4 lg:p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-lcars-blue/30 pb-3">
                <h3 className="text-base font-bold text-lcars-blue uppercase tracking-wider flex items-center gap-2">
                  <FileText className="w-5 h-5 text-lcars-gold" />
                  Mission Summary & Tactical Lessons Learned
                </h3>
                {isDebriefEditing && (
                  <button
                    onClick={handleSaveDebrief}
                    disabled={isSavingDebrief}
                    className="lcars-pill bg-lcars-blue text-black px-4 py-1 text-xs font-bold hover:bg-white transition-colors"
                  >
                    {isSavingDebrief ? 'SAVING...' : 'SAVE DEBRIEF'}
                  </button>
                )}
              </div>

              {isDebriefEditing ? (
                <div className="space-y-4 font-sans">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-lcars-blue uppercase tracking-wider font-lcars">
                      Mission Summary / After-Action Report
                    </label>
                    <textarea
                      value={eventSummary}
                      onChange={e => setEventSummary(e.target.value)}
                      placeholder="Write an after-action summary of the event activities and attendance..."
                      className="w-full bg-[#0d1117] border border-border-color focus:border-accent-blue rounded p-3 text-sm text-[var(--foreground)] min-h-[80px]"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-lcars-gold uppercase tracking-wider font-lcars">
                      Tactical Lessons Learned
                    </label>
                    <textarea
                      value={eventLessonsLearned}
                      onChange={e => setEventLessonsLearned(e.target.value)}
                      placeholder="Document operational takeaways, improvements for future events, etc..."
                      className="w-full bg-[#0d1117] border border-border-color focus:border-accent-blue rounded p-3 text-sm text-[var(--foreground)] min-h-[70px]"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                    <div className="text-xs font-bold text-lcars-blue uppercase tracking-wider mb-1">
                      Mission Summary
                    </div>
                    <p className="text-sm text-[var(--foreground)]/80 leading-relaxed whitespace-pre-wrap font-sans">
                      {selectedEvent.summary || <span className="italic text-[var(--foreground)]/40">No summary recorded.</span>}
                    </p>
                  </div>
                  <div className="p-3 bg-white/5 border border-white/10 rounded-lg">
                    <div className="text-xs font-bold text-lcars-gold uppercase tracking-wider mb-1">
                      Lessons Learned
                    </div>
                    <p className="text-sm text-[var(--foreground)]/80 leading-relaxed whitespace-pre-wrap font-sans">
                      {selectedEvent.lessonsLearned || <span className="italic text-[var(--foreground)]/40">No lessons learned recorded.</span>}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Confirmed Attendees Table */}
          <div className="overflow-x-auto">
            <div className="mb-2">
              <span className="text-xs font-bold text-lcars-tan uppercase tracking-wider">
                Confirmed Crew Roster ({attendees.length})
              </span>
            </div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-lcars-tan text-[0.7rem] font-bold tracking-widest uppercase">
                  <th className="text-left p-4 border-b-2 border-lcars-tan/30">FIRST NAME</th>
                  <th className="text-left p-4 border-b-2 border-lcars-tan/30">LAST NAME</th>
                  <th className="text-left p-4 border-b-2 border-lcars-tan/30">EMAIL</th>
                  <th className="text-left p-4 border-b-2 border-lcars-tan/30">CELL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-lcars-tan/10">
                {attendees.map(({ user: u, attendanceId }) => (
                  <tr key={u.uid} className="hover:bg-lcars-tan/5 transition-colors group">
                    <td className="p-4 font-bold text-lg group-hover:text-lcars-tan transition-colors">{u.firstName}</td>
                    <td className="p-4 font-bold text-lg group-hover:text-lcars-tan transition-colors">{u.lastName}</td>
                    <td className="p-4 text-[var(--foreground)]/60 font-bold text-sm">{u.email}</td>
                    <td className="p-4 text-[var(--foreground)]/60 font-bold text-sm">
                      <div className="flex items-center justify-between">
                        <span>{u.cell || 'N/A'}</span>
                        {attendanceId && (
                          <button
                            onClick={() => handleRemoveAttendance(attendanceId, `${u.firstName} ${u.lastName}`)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-lcars-red hover:bg-lcars-red/20 rounded ml-2"
                            title="Remove from attendance"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {attendees.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-12 text-[var(--foreground)]/40 font-bold tracking-widest italic">
                      NO CONFIRMED PERSONNEL RECORDED FOR THIS MISSION.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-[var(--foreground)]/40 font-bold tracking-widest uppercase">
          No missions available.
        </div>
      )}

      {/* Add Attendees Modal */}
      {isAddModalOpen && selectedEvent && (
        <Dialog open={true} onOpenChange={() => setIsAddModalOpen(false)}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto bg-[#111] border-2 border-lcars-gold text-[var(--foreground)] font-lcars p-6">
            <DialogHeader className="border-b border-lcars-gold/30 pb-3">
              <DialogTitle className="text-xl font-bold text-lcars-gold uppercase tracking-tighter">
                LOG ATTENDANCE: {cleanIcsString(selectedEvent.name)}
              </DialogTitle>
              <DialogDescription className="text-xs text-[var(--foreground)]/60 font-sans">
                Select crew members who attended this mission/activity.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-3">
              {/* Search Bar */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-[var(--foreground)]/40" />
                <Input
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search crew members by name, email, or SCC..."
                  className="pl-9 bg-[var(--background)] border-white/20 font-sans text-sm"
                />
              </div>

              {/* Members Checklist */}
              <div className="max-h-72 overflow-y-auto divide-y divide-white/5 border border-white/10 rounded bg-black/40 p-2">
                {filteredUsers.map(u => {
                  const isAlreadyAttending = attendingUserIds.has(u.uid);
                  const isChecked = selectedUserIds.has(u.uid);

                  return (
                    <div 
                      key={u.uid} 
                      className={`flex items-center justify-between p-2.5 rounded transition-colors ${
                        isAlreadyAttending ? 'opacity-50 bg-white/5' : 'hover:bg-white/5 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (!isAlreadyAttending) toggleUserSelection(u.uid);
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox 
                          checked={isAlreadyAttending || isChecked}
                          disabled={isAlreadyAttending}
                          onCheckedChange={() => {
                            if (!isAlreadyAttending) toggleUserSelection(u.uid);
                          }}
                        />
                        <div>
                          <p className="font-bold text-sm">
                            {u.firstName} {u.lastName}
                          </p>
                          <p className="text-[0.65rem] text-[var(--foreground)]/50 font-mono">
                            {u.email} {u.sccNumber ? `• SCC ${u.sccNumber}` : ''}
                          </p>
                        </div>
                      </div>

                      {isAlreadyAttending ? (
                        <Badge className="bg-lcars-blue/20 text-lcars-blue border border-lcars-blue/40 text-[0.6rem] uppercase">
                          Already Logged
                        </Badge>
                      ) : (
                        <span className="text-xs text-lcars-gold font-mono font-bold">
                          +{selectedEvent.points || 10} PTS
                        </span>
                      )}
                    </div>
                  );
                })}

                {filteredUsers.length === 0 && (
                  <p className="text-center py-6 text-xs text-[var(--foreground)]/40 italic">
                    No crew members match search.
                  </p>
                )}
              </div>
            </div>

            <DialogFooter className="flex justify-between items-center pt-2 border-t border-white/10">
              <span className="text-xs text-[var(--foreground)]/60 font-mono">
                {selectedUserIds.size} selected
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddModalOpen(false)}
                  className="border-white/20 text-xs uppercase"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAddAttendees}
                  disabled={selectedUserIds.size === 0 || isSubmitting}
                  className="bg-lcars-gold hover:bg-lcars-gold/80 text-black font-bold text-xs uppercase tracking-wider"
                >
                  {isSubmitting ? 'Logging...' : `Log Attendance (${selectedUserIds.size})`}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmDialog.isOpen && (
        <LCARSConfirmationModal
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          description={confirmDialog.description}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        />
      )}
    </div>
  );
}

export default AdminDashboard;
